// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { Link } from "react-router-dom"
import { GuardRokIcon } from "../components/GuardRokLogo"

const LAST_UPDATED = "May 18, 2026 (v2.0)"

export default function Terms() {
  return (
    <div style={{ minHeight: "100vh", background: "#030712", color: "#fff", fontFamily: "system-ui, sans-serif" }}>

      {/* Nav */}
      <nav style={{ padding: "20px 48px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #0f172a", position: "sticky", top: 0, background: "rgba(3,9,18,0.95)", backdropFilter: "blur(12px)", zIndex: 100 }}>
        <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
          <GuardRokIcon size={36} />
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: "-0.5px", lineHeight: 1, color: "#fff" }}>Guard<span style={{ color: "#E63946" }}>Rok</span><span style={{ fontSize: 8, color: "#6b7280", verticalAlign: "super", marginLeft: 1 }}>™</span></div>
            <div style={{ fontSize: 9, letterSpacing: "3px", color: "#6b7280", textTransform: "uppercase" as const, marginTop: 2 }}>AI Firewall</div>
          </div>
        </Link>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <Link to="/docs"    style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Docs</Link>
          <Link to="/pricing" style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Pricing</Link>
          <Link to="/login"   style={{ color: "#9ca3af", fontSize: 14, textDecoration: "none" }}>Sign in</Link>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: 760, margin: "0 auto", padding: "64px 40px 96px" }}>
        <div style={{ fontSize: 12, color: "#475569", marginBottom: 12 }}>Last updated: {LAST_UPDATED}</div>
        <h1 style={{ fontSize: 36, fontWeight: 900, letterSpacing: "-1px", marginBottom: 10 }}>Terms of Service</h1>
        <p style={{ color: "#64748b", fontSize: 15, marginBottom: 24, lineHeight: 1.7 }}>
          These Terms of Service ("Terms") govern your use of GuardRok, operated by aiApas LLC ("Company", "we", "us"). By creating an account or installing the GuardRok extension, you agree to these Terms.
        </p>
        <div style={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 10, padding: "16px 20px", marginBottom: 48 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#f59e0b", textTransform: "uppercase" as const, letterSpacing: "0.08em", marginBottom: 6 }}>Beta Programme Notice (v2.0)</div>
          <div style={{ fontSize: 13, color: "#94a3b8", lineHeight: 1.7 }}>GuardRok is currently in beta. Features may change, detection coverage is not exhaustive, and the service should not be relied upon as your sole compliance control. Data Processing Agreements (DPA) and Business Associate Agreements (BAA) are available on request at <a href="mailto:hello@guardrok.com" style={{ color: "#E63946" }}>hello@guardrok.com</a>.</div>
        </div>

        {[
          {
            title: "1. The service",
            body: [
              "GuardRok provides an AI firewall that stops sensitive employee prompts before they reach third-party AI tools, detects sensitive data, and enforces your organisation's policies.",
              "The service consists of a Chrome extension, a web dashboard, and Supabase-hosted backend infrastructure.",
              "GuardRok is a B2B product. The purchasing organisation ('Customer') is responsible for ensuring its employees use the service in compliance with applicable law.",
            ],
          },
          {
            title: "2. Accounts and access",
            body: [
              "You must provide accurate information when creating an account.",
              "You are responsible for maintaining the confidentiality of your credentials.",
              "One seat equals one employee with the GuardRok extension installed and active.",
              "You must not share accounts, reverse-engineer the service, or attempt to circumvent policy enforcement.",
            ],
          },
          {
            title: "3. Acceptable use",
            body: [
              "GuardRok may only be used for lawful employee monitoring consistent with applicable employment law in your jurisdiction.",
              "Customers deploying GuardRok are responsible for notifying employees that AI tool usage is monitored, as required by local law.",
              "You may not use GuardRok to monitor personal devices without explicit employee consent.",
              "You may not attempt to bypass, disable, or tamper with GuardRok's detection engine.",
            ],
          },
          {
            title: "4. Data ownership",
            body: [
              "Your organisation owns all prompt log data generated by your employees.",
              "We do not claim any rights over your data.",
              "Upon account termination, data is retained for 30 days then permanently deleted, unless you request earlier deletion.",
            ],
          },
          {
            title: "5. Payment and plans",
            body: [
              "Free plan: no payment required. Features and seat limits apply as shown on the Pricing page.",
              "Paid plans: billed monthly. Pricing is as listed at guardrok.com/pricing at time of subscription.",
              "Annual plans receive a 2-month discount as described on the Pricing page.",
              "You may cancel at any time. No refunds for partial months on monthly plans.",
              "We reserve the right to change pricing with 30 days' notice.",
            ],
          },
          {
            title: "6. Uptime and support",
            body: [
              "We target 99.5% monthly uptime for the dashboard and Edge Functions.",
              "Support is provided by email at hello@guardrok.com. Response times vary by plan.",
              "We are not liable for downtime caused by third-party AI tools (ChatGPT, Copilot, Gemini, Claude) or Supabase infrastructure.",
            ],
          },
          {
            title: "7. Intellectual property",
            body: [
              "GuardRok and all associated software, logos, and content are owned by aiApas LLC.",
              "A patent application has been filed (USPTO Application #64/036,473, filed April 11, 2026).",
              "You may not copy, distribute, or create derivative works without written permission.",
            ],
          },
          {
            title: "8. Disclaimer of warranties",
            body: [
              "GuardRok is provided 'as is' and 'as available' without warranty of any kind, express or implied.",
              "GuardRok is a best-effort control layer. We do not guarantee that all sensitive data will be detected or blocked. Detection accuracy depends on policy configuration, prompt structure, and the evolving nature of AI tool interfaces.",
              "GuardRok does not guarantee regulatory compliance. Deploying GuardRok does not make your organisation HIPAA-compliant, GDPR-compliant, or compliant with any other regulation. Compliance is the responsibility of your organisation.",
              "GuardRok is not a substitute for legal counsel, compliance programmes, employee training, or other required controls.",
              "We do not guarantee uninterrupted service. Third-party AI tools (ChatGPT, Copilot, Gemini, Claude) may update their interfaces in ways that temporarily affect GuardRok's interception capability.",
            ],
          },
          {
            title: "9. Limitation of liability",
            body: [
              "To the maximum extent permitted by law, aiApas LLC's total cumulative liability for any claims arising under or related to these Terms is limited to the fees paid by you in the 3 months immediately preceding the claim.",
              "We are not liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to: loss of data, loss of revenue, regulatory fines, penalties imposed by any government or regulatory authority, or costs of breach notification.",
              "This limitation applies regardless of the legal theory under which the claim is brought and even if we have been advised of the possibility of such damages.",
              "Some jurisdictions do not allow the exclusion of certain warranties or limitation of liability — in such cases, our liability is limited to the maximum extent permitted by applicable law.",
            ],
          },
          {
            title: "9a. Indemnification",
            body: [
              "You agree to indemnify, defend, and hold harmless aiApas LLC from any claims, losses, damages, or expenses (including legal fees) arising from: (a) your use of GuardRok in violation of these Terms; (b) your failure to obtain required employee consent for monitoring; (c) your violation of applicable law; or (d) any claim by your employees related to prompt interception.",
            ],
          },
          {
            title: "9b. Third-party platform dependency",
            body: [
              "GuardRok operates as a Chrome browser extension and depends on Google Chrome and the Chrome Web Store. We do not control Google's policies and cannot guarantee continued availability of the extension on the Chrome Web Store.",
              "In the event of a Chrome Web Store removal, we will notify customers within 24 hours and provide alternative deployment options where technically feasible.",
            ],
          },
          {
            title: "10. Termination",
            body: [
              "Either party may terminate at any time with 30 days' written notice.",
              "We may suspend or terminate accounts that violate these Terms without notice.",
              "On termination, your right to use GuardRok ceases immediately.",
            ],
          },
          {
            title: "11. Governing law",
            body: [
              "These Terms are governed by the laws of the State of Texas, USA.",
              "Any disputes shall be resolved in the courts of Dallas County, Texas.",
            ],
          },
          {
            title: "12. Changes to these Terms",
            body: [
              "We will notify organisation admins by email at least 14 days before material changes take effect.",
              "Continued use after that date constitutes acceptance of the updated Terms.",
            ],
          },
          {
            title: "13. Contact",
            body: [
              "aiApas LLC · hello@guardrok.com",
            ],
          },
        ].map(({ title, body }) => (
          <div key={title} style={{ marginBottom: 36 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 12 }}>{title}</h2>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column" as const, gap: 8 }}>
              {body.map((line, i) => (
                <li key={i} style={{ fontSize: 14, color: "#94a3b8", lineHeight: 1.75, paddingLeft: 16, position: "relative" as const }}>
                  <span style={{ position: "absolute" as const, left: 0, color: "#374151" }}>·</span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #0f172a", padding: "20px 48px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 12, color: "#374151" }}>© 2026 GuardRok™ · aiApas LLC</div>
        <div style={{ display: "flex", gap: 20 }}>
          <Link to="/privacy" style={{ fontSize: 12, color: "#4b5563", textDecoration: "none" }}>Privacy</Link>
          <Link to="/cookies" style={{ fontSize: 12, color: "#4b5563", textDecoration: "none" }}>Cookies</Link>
          <a href="mailto:hello@guardrok.com" style={{ fontSize: 12, color: "#4b5563", textDecoration: "none" }}>Contact</a>
        </div>
      </div>
    </div>
  )
}
